import { toRadian } from '../utils/string.utilts';
import { LayoutBase } from './LayoutBase';

export default class extends LayoutBase {

  getSize(str?: string): { width: number; height: number } {
    str = str ?? this.getDataStr();
    const { space, decimalOffsetX, unitIcon, ref } = this
      .dataItem as IDataItemImageNumber;

    const { children = [] } = this.runtime.getDataItem<IImageArray>(ref) ?? {};

    let width = 0;
    // 是否需要小数点补零
    if (str.includes('.')) width += decimalOffsetX;

    const unitIconImgEl = this.runtime.getDataItem<IImage>(unitIcon)?.imgEl;

    // 计算每个图片的宽度
    let height = 0;
    for (let i = 0; i < str.length; i++) {
      const _str = str[i];
      const index = _str === '-' ? 10 : _str === '.' ? 11 : Number(_str);
      const imgEl =
        children[index]?.imgEl ??
        children.filter((item) => !!item.imgEl)?.[0]?.imgEl;
      width += imgEl?.width ?? 0;
      height = Math.max(height, imgEl?.height ?? 0);
    }
    if (unitIconImgEl) {
      width = width + unitIconImgEl.width;
      if (unitIconImgEl.height > height) height = unitIconImgEl.height;
    }

    // 考虑缩放值，space 不参与缩放
    const { _scale = 1 } = this;
    const spaceCount = str.length - 1 + (unitIconImgEl ? 1 : 0);
    width = width * _scale + spaceCount * space;
    height = height * _scale;
    return { width, height };
  }

  private drawOffscreenNum(params: {
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
    str: string;
    images: IImage[];
    space: number;
    decimalOffsetX: number;
    scale?: number;
  }) {
    const { ctx, str, images, space, decimalOffsetX, scale = 1 } = params;

    let x = 0;
    for (let i = 0; i < str.length; i++) {
      const num = str[i];
      const imageIndex = num === '-' ? 10 : num === '.' ? 11 : parseInt(num);
      const image = images[imageIndex]?.imgEl;
      if (!image) continue;

      // 如果不是第一个图片，需要考虑图片间距
      // 字间距也需要乘以 scale，与 getSize() 的计算保持一致
      if (i > 0) x += (space ?? 0);

      // 如果是小数点，需要考虑偏移量
      // 小数点偏移量也需要乘以 scale，与 getSize() 的计算保持一致
      if (imageIndex == 11) {
        x = x + (decimalOffsetX ?? 0) * scale;
      }

      const w = image.width * scale;
      const h = image.height * scale;
      this.drawRecolorImage(ctx, image, { x, y: 0, w, h });
      // 绘制完成后移动坐标x
      x += w;
    }
  }

  private drawUnitIcon(data: {
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
    unitIcon: HTMLImageElement;
    wholeWidth: number;
    scale?: number;
    space?: number;
  }) {
    const { unitIcon, ctx, wholeWidth, scale = 1 } = data;

    const imageWidth = unitIcon.width * scale;
    const imageHeight = unitIcon.height * scale;

    // 单位图标的位置需要考虑与数字之间的间距
    // getSize() 计算时添加了 space，所以这里也需要考虑 space * scale
    const x = wholeWidth - imageWidth;
    const y = 0;
    this.drawRecolorImage(ctx, unitIcon, {
      x,
      y,
      w: imageWidth,
      h: imageHeight
    });
  }

  private getDataStr() {
    const node = this.dataItem as IDataItemImageNumber;
    // 前面补0
    function setLeadingZero(str: string, totalLength: number): string {
      let result = str;
      for (let i = 0; i < totalLength; i++) {
        if (result[0] === '-') result = '-0' + result.substr(1, result.length);
        else result = '0' + result;
      }
      return result;
    }

    // 后面补0
    function setTrailingZero(str: string, totalLength: number): string {
      let result = str;
      for (let i = 0; i < totalLength; i++) result += '0';
      return result;
    }

    const { decimalDigits = 0, totalDigits = 0, source } = node;
    const data = this.getDataSourceValue(source) as number;
    if (Number.isNaN(data))
      return '-'.padStart(
        totalDigits - (decimalDigits > 0 ? decimalDigits + 1 : 0),
        '-'
      );

    let str =
      decimalDigits !== 0 && String(data).includes('.')
        ? data.toFixed(decimalDigits)
        : Math.floor(data).toString();
    const totalLength = totalDigits - str.length;
    str = String(Number(str));
    // 前面补0
    if (node.leadingZero && totalLength > 0)
      str = setLeadingZero(str, totalLength);

    // 后面补0
    if (node.trailingZero) {
      if (!str.includes('.')) str += '.';
      const _decimalDigits = str.split('.')?.[1]?.length ?? 0;
      const trailingZeroTotal = decimalDigits - _decimalDigits;
      str = setTrailingZero(str, trailingZeroTotal);
    }
    return str;
  }
  draw(): void {
    const { ctx, runtime, dataItem, _scale = 1, _alpha = 1 } = this;
    const node = dataItem as IDataItemImageNumber;
    const {
      source,
      ref: imgArrRef,
      unitIcon: unitIconRef,
      space = 0,
      decimalOffsetX = 0,
      rotation = 0,
      align = 'left'
    } = node;
    if (!source) return;

    // 获取要渲染的数值字符串
    const str = this.getDataStr();

    const imageArrItem = runtime.getDataItem<IImageArray>(imgArrRef);
    if (!imageArrItem?.children.length) return;

    const unitIconEl = runtime.getDataItem<IImage>(unitIconRef)?.imgEl;

    const dirtyImages = imageArrItem.children.map((i) => i.src);
    if (unitIconEl) dirtyImages.push(unitIconEl.src);

    const { width, height } = this.getSize(str);
    if (!width || !height) return;

    // 透明度
    ctx.globalAlpha = _alpha;

    // 初始化离屏画布
    const { canvas: offscreenCanvas } = this.getOffscreen({
      width,
      height,
      init: (_, ctx) => {
        // 绘制离屏数字
        this.drawOffscreenNum({
          ctx,
          str,
          images: imageArrItem.children,
          space,
          decimalOffsetX,
          scale: _scale
        });

        // 绘制单位图标
        if (unitIconEl)
          this.drawUnitIcon({
            unitIcon: unitIconEl,
            ctx,
            wholeWidth: width,
            scale: _scale,
            space
          });
      },
      dirtyParmas: {
        str,
        imgArrRef,
        unitIconRef,
        space,
        decimalOffsetX,
        images: dirtyImages.join(''),
        _scale,
        _alpha,
        width,
        height
      }
    });

    ctx.save();
    const offsetX =
      (align == 'center' ? -0.5 : align == 'right' ? -1 : 0) * width;

    this.drawAniFrame(offscreenCanvas, {
      selfOffset: { offsetX },
      selfRotation: {
        rotation: toRadian(rotation),
        pivotX: 0,
        pivotY: 0
      }
    });
    ctx.restore();
    ctx.globalAlpha = 1;

    this.setRuntimeData({
      width,
      height,
      rotation
    });
  }
}
